import { defaultTransitionConfig } from "@app/configs/animation";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Layout = styled.div`
  z-index: 9999;
  position: absolute;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  align-content: center;
  overflow: hidden;
  top: 10px;
  width: calc(100% - 40px);
  padding: var(--spacing-s) 0;
`;

const Container = styled(motion.div)`
  width: auto;
  background: rgba(59, 130, 246, 0.1);
  border-radius: var(--border-radius-m);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  align-content: center;
  gap: var(--spacing-s);
  padding: var(--spacing-s) var(--spacing-m);
  font: var(--font-m);
  line-height: 1.2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  position: relative;
  overflow: visible;
`;

const IconSvg = styled(motion.svg)`
  width: 18px;
  height: 18px;
`;

const CirclePath = styled(motion.circle)`
  fill: none;
  stroke-linecap: round;
`;

const CheckPath = styled(motion.path)`
  fill: none;
  stroke: #52c41a;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const Text = styled.p`
  margin: 0;
  color: #fff;
  font-size: 14px;
`;

type Props = {
  isConnecting: boolean;
  isComplete: boolean;
  onHide: () => void;
};

const ConnectionNotification = ({
  isConnecting,
  isComplete,
  onHide,
}: Props) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hide, setHide] = useState(false);

  useEffect(() => {
    if (!isConnecting && !isComplete) {
      setHide(true);
      return;
    }

    setHide(false);

    // If connection is complete, hide after 2 seconds
    if (isComplete) {
      timeoutRef.current = setTimeout(() => {
        setHide(true);
        setTimeout(() => {
          onHide();
        }, 300); // Wait for animation to complete
      }, 2000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isConnecting, isComplete, onHide]);

  if (!isConnecting && !isComplete) return null;

  return (
    <Layout>
      <Container
        animate={{
          y: hide ? -40 : 0,
          opacity: hide ? 0 : 1,
        }}
        initial={{ y: -40, opacity: 0 }}
        transition={defaultTransitionConfig}
      >
        <IconWrapper>
          <IconSvg
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              scale: {
                type: "spring",
                stiffness: 200,
                damping: 15,
              },
              opacity: {
                duration: 0.2,
              },
            }}
          >
            <CirclePath
              cx="12"
              cy="12"
              r="9"
              strokeWidth="2.5"
              strokeLinecap="round"
              initial={
                !isComplete
                  ? {
                      stroke: "#3b82f6",
                      strokeDasharray: "35 35",
                      strokeDashoffset: -70,
                      rotate: 180,
                      opacity: 1,
                    }
                  : {
                      stroke: "#52c41a",
                      strokeDasharray: "56.55",
                      strokeDashoffset: 0,
                      rotate: 0,
                      opacity: 1,
                    }
              }
              animate={{
                stroke: isComplete ? "#52c41a" : "#3b82f6",
                strokeDasharray: isComplete ? "56.55" : "35 35",
                strokeDashoffset: isComplete ? 0 : [0, -140],
                rotate: isComplete ? 0 : 360,
                opacity: 1,
              }}
              transition={{
                stroke: {
                  duration: 0.5,
                  ease: "easeInOut",
                },
                strokeDasharray: {
                  duration: 0.5,
                  ease: "easeInOut",
                },
                strokeDashoffset: isComplete
                  ? {
                      duration: 0.5,
                      ease: "easeInOut",
                    }
                  : {
                      repeat: Infinity,
                      duration: 2,
                      ease: "linear",
                      repeatType: "loop",
                      repeatDelay: 0,
                    },
                rotate: isComplete
                  ? {
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      mass: 0.5,
                    }
                  : {
                      repeat: Infinity,
                      duration: 2,
                      ease: "linear",
                      repeatType: "loop",
                      repeatDelay: 0,
                    },
              }}
              style={{ transformOrigin: "12px 12px" }}
            />
            {isComplete && (
              <CheckPath
                d="M8 12l2 2 4-4"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.3,
                  ease: "easeOut",
                }}
              />
            )}
          </IconSvg>
        </IconWrapper>
        <Text style={{ minWidth: 94 }}>
          {isComplete ? "Đã kết nối" : "Đang kết nối ..."}
        </Text>
      </Container>
    </Layout>
  );
};

export { ConnectionNotification };
